import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from './navbar';
import Footer from './Footer';
import { FiArrowLeft, FiExternalLink, FiDollarSign, FiMapPin, FiUsers, FiTrendingUp, FiBriefcase } from 'react-icons/fi';
import { STARTUP_API_URL } from '../apiConfig';
import { motion } from 'framer-motion';
import RippleWaveLoader from './ui/RippleWaveLoader';

const VC_DETAIL_LOADING_MESSAGES = [
  "Loading VC profile…",
  "Fetching portfolio companies…",
  "Analyzing investment history…",
  "Gathering sector focus data…",
];

interface VCProfile {
  id?: number;
  name: string;
  description?: string;
  website?: string;
  linkedin_url?: string;
  headquarters?: string;
  aum?: string;
  investment_focus?: string[];
  stage_focus?: string;
  check_size_min?: string;
  check_size_max?: string;
  geographic_focus?: string[];
  portfolio_count?: number;
  founded_year?: string;
  partners?: Array<{
    name: string;
    title: string;
    linkedin_url?: string;
    focus_areas?: string[];
  }>;
  investments?: Array<{
    startup_name: string;
    round_type?: string;
    amount?: string;
    investment_date?: string;
  }>;
  source?: string;
}

const VCDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<VCProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'investments' | 'partners'>('overview');

  useEffect(() => {
    if (id) {
      fetchVCProfile(id);
    }
  }, [id]);

  const fetchVCProfile = async (vcId: string) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${STARTUP_API_URL}/api/v1/vc/profile/${vcId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken') || ''
        }
      });

      const data = await response.json();

      if (data.status === 'success') {
        setProfile(data.data);
      } else {
        setError(data.message || 'Failed to fetch VC profile');
      }
    } catch (err) {
      setError('Error fetching VC profile');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      <div className="pt-24 pb-16 px-4 md:px-12 lg:px-24">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <FiArrowLeft size={20} />
          Back to VC Intelligence
        </button>

        {loading && (
          <div className="py-20">
            <RippleWaveLoader messages={VC_DETAIL_LOADING_MESSAGES} />
          </div>
        )}

        {error && (
          <div className="text-center py-12">
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={() => id && fetchVCProfile(id)}
              className="px-4 py-2 bg-[#FF541F] rounded-lg hover:bg-[#FF541F]/80 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && profile && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-5xl mx-auto"
          >
            {/* Header */}
            <div className="bg-[#1A1A1A] rounded-xl p-8 mb-6 border border-white/10">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold mb-2">{profile.name}</h1>
                  <p className="text-gray-400 text-lg max-w-2xl">{profile.description || 'No description available'}</p>
                </div>

                <div className="flex gap-3">
                  {profile.website && (
                    <a
                      href={profile.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                    >
                      <FiExternalLink size={18} />
                      Website
                    </a>
                  )}
                  {profile.linkedin_url && (
                    <a
                      href={profile.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                    >
                      LinkedIn
                    </a>
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                {profile.aum && (
                  <div className="bg-black/30 rounded-lg p-4">
                    <p className="text-gray-500 text-sm mb-1">AUM</p>
                    <p className="text-[#FF541F] font-semibold flex items-center gap-2">
                      <FiDollarSign size={16} />
                      {profile.aum}
                    </p>
                  </div>
                )}
                {profile.stage_focus && (
                  <div className="bg-black/30 rounded-lg p-4">
                    <p className="text-gray-500 text-sm mb-1">Stage Focus</p>
                    <p className="text-white font-semibold flex items-center gap-2">
                      <FiTrendingUp size={16} className="text-green-400" />
                      {profile.stage_focus}
                    </p>
                  </div>
                )}
                {(profile.check_size_min || profile.check_size_max) && (
                  <div className="bg-black/30 rounded-lg p-4">
                    <p className="text-gray-500 text-sm mb-1">Check Size</p>
                    <p className="text-white font-semibold">
                      {profile.check_size_min} - {profile.check_size_max}
                    </p>
                  </div>
                )}
                {profile.portfolio_count !== undefined && (
                  <div className="bg-black/30 rounded-lg p-4">
                    <p className="text-gray-500 text-sm mb-1">Portfolio</p>
                    <p className="text-white font-semibold flex items-center gap-2">
                      <FiBriefcase size={16} className="text-purple-400" />
                      {profile.portfolio_count} Companies
                    </p>
                  </div>
                )}
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-6">
                {profile.headquarters && (
                  <span className="px-3 py-1 bg-white/10 text-gray-300 rounded-full text-sm flex items-center gap-1">
                    <FiMapPin size={14} />
                    {profile.headquarters}
                  </span>
                )}
                {profile.founded_year && (
                  <span className="px-3 py-1 bg-white/10 text-gray-300 rounded-full text-sm">
                    Founded {profile.founded_year}
                  </span>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-4 mb-6 border-b border-white/10 pb-2">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  activeTab === 'overview' ? 'bg-[#FF541F] text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('investments')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  activeTab === 'investments' ? 'bg-[#FF541F] text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                Investments
              </button>
              <button
                onClick={() => setActiveTab('partners')}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  activeTab === 'partners' ? 'bg-[#FF541F] text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                Partners
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Investment Focus */}
                {profile.investment_focus && profile.investment_focus.length > 0 && (
                  <div className="bg-[#1A1A1A] rounded-xl p-6 border border-white/10">
                    <h2 className="text-xl font-semibold mb-4">Investment Focus</h2>
                    <div className="flex flex-wrap gap-2">
                      {profile.investment_focus.map((focus, index) => (
                        <span key={index} className="px-3 py-1 bg-[#FF541F]/20 text-[#FF541F] rounded-lg text-sm">
                          {focus}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Geographic Focus */}
                {profile.geographic_focus && profile.geographic_focus.length > 0 && (
                  <div className="bg-[#1A1A1A] rounded-xl p-6 border border-white/10">
                    <h2 className="text-xl font-semibold mb-4">Geographic Focus</h2>
                    <div className="flex flex-wrap gap-2">
                      {profile.geographic_focus.map((geo, index) => (
                        <span key={index} className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-lg text-sm flex items-center gap-1">
                          <FiMapPin size={14} />
                          {geo}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'investments' && (
              <div className="bg-[#1A1A1A] rounded-xl p-6 border border-white/10">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <FiDollarSign className="text-green-400" />
                  Recent Investments
                </h2>
                {profile.investments && profile.investments.length > 0 ? (
                  <div className="space-y-3">
                    {profile.investments.map((investment, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-black/30 rounded-lg">
                        <div>
                          <p className="font-medium text-white">{investment.startup_name}</p>
                          <div className="flex gap-2 mt-1">
                            {investment.round_type && (
                              <span className="text-sm text-gray-400">{investment.round_type}</span>
                            )}
                            {investment.investment_date && (
                              <span className="text-sm text-gray-500">{investment.investment_date}</span>
                            )}
                          </div>
                        </div>
                        {investment.amount && (
                          <span className="text-green-400 font-semibold">{investment.amount}</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400">No investment data available</p>
                )}
              </div>
            )}

            {activeTab === 'partners' && (
              <div className="bg-[#1A1A1A] rounded-xl p-6 border border-white/10">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <FiUsers className="text-blue-400" />
                  Partners & Team
                </h2>
                {profile.partners && profile.partners.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.partners.map((partner, index) => (
                      <div key={index} className="p-4 bg-black/30 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-white">{partner.name}</p>
                            <p className="text-sm text-gray-400">{partner.title}</p>
                          </div>
                          {partner.linkedin_url && (
                            <a
                              href={partner.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <FiExternalLink size={16} />
                            </a>
                          )}
                        </div>
                        {partner.focus_areas && partner.focus_areas.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {partner.focus_areas.map((area, i) => (
                              <span key={i} className="px-2 py-0.5 bg-white/5 text-gray-400 rounded text-xs">
                                {area}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400">No partner data available</p>
                )}
              </div>
            )}
          </motion.div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default VCDetailPage;
